import React, { createContext, useCallback, useMemo, useState } from "react";
import { GlobalStyles } from "@mui/material";
import { FileItem } from "../../types/interfaces/filesStorage";
import FilePreviewModal from "./FilePreviewModal";
import { PreviewOpenOptions, PreviewSource } from "./previewSource";

const previewGlobalStyles = (
  <GlobalStyles
    styles={{
      "mark.preview-search-mark": {
        backgroundColor: "#ffe066",
        color: "inherit",
        padding: "0 1px",
        borderRadius: 2,
        boxShadow: "0 0 0 1px rgba(0,0,0,0.05)",
        transition: "background-color 0.15s ease, color 0.15s ease",
      },
      "mark.preview-search-mark.preview-search-mark-active": {
        backgroundColor: "#ff9800",
        color: "#ffffff",
        boxShadow: "0 0 0 2px rgba(255,152,0,0.45)",
      },
    }}
  />
);

interface PreviewStackEntry {
  source: PreviewSource;
}

export interface FilePreviewContextValue {
  openPreview: (item: FileItem, options?: PreviewOpenOptions) => void;
  openInlinePreview: (
    name: string,
    blob: Blob,
    options?: PreviewOpenOptions & { size?: number },
  ) => void;
  closePreview: () => void;
  closeAll: () => void;
}

export const FilePreviewContext = createContext<FilePreviewContextValue | null>(
  null,
);

interface ProviderProps {
  children: React.ReactNode;
}

export const FilePreviewProvider: React.FC<ProviderProps> = ({ children }) => {
  const [stack, setStack] = useState<PreviewStackEntry[]>([]);

  const openPreview = useCallback(
    (item: FileItem, options?: PreviewOpenOptions) => {
      const src: PreviewSource = {
        kind: "remote",
        item,
        ...(options?.onBack != null
          ? ({ onBack: options.onBack, backLabel: options.backLabel } as Partial<PreviewSource>)
          : {}),
      } as PreviewSource;
      setStack((prev) => [...prev, { source: src }]);
    },
    [],
  );

  const openInlinePreview = useCallback(
    (
      name: string,
      blob: Blob,
      options?: PreviewOpenOptions & { size?: number },
    ) => {
      const src: PreviewSource = {
        kind: "inline",
        name,
        blob,
        size: options?.size,
        onBack: options?.onBack,
        backLabel: options?.backLabel,
      };
      setStack((prev) => [...prev, { source: src }]);
    },
    [],
  );

  const closePreview = useCallback(() => {
    setStack((prev) => prev.slice(0, -1));
  }, []);

  const closeAll = useCallback(() => {
    setStack([]);
  }, []);

  const value = useMemo<FilePreviewContextValue>(
    () => ({ openPreview, openInlinePreview, closePreview, closeAll }),
    [openPreview, openInlinePreview, closePreview, closeAll],
  );

  return (
    <FilePreviewContext.Provider value={value}>
      {previewGlobalStyles}
      {children}
      {stack.map((entry, idx) => (
        <FilePreviewModal
          key={idx}
          open
          source={entry.source}
          onClose={idx === stack.length - 1 ? closePreview : () => undefined}
          onCloseAll={closeAll}
          stackIndex={idx}
          stackSize={stack.length}
        />
      ))}
    </FilePreviewContext.Provider>
  );
};
